import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import Stamp from './layout/Stamp';

interface AchievementDocument {
  id: string;
  url: string;
  fileName: string;
  uploadedAt?: string;
  status?: string; // pending | verified | rejected
}

interface Achievement {
  id: string;
  title: string;
  category?: string;
  activityTier?: string | null;
  verificationStatus?: string; // pending | verified | self_reported | rejected
  documents?: AchievementDocument[];
}

interface MentorProfile {
  id: string;
  displayName?: string;
  pricePerSession?: number;
  averageRating?: number;
  totalReviews?: number;
  isVerified?: boolean;
  profileViews?: number;
  achievements?: Achievement[];
}

const CLAIM_STATUS: Record<string, { label: string; variant: 'verified' | 'selfrep' }> = {
  verified: { label: 'Verified', variant: 'verified' },
  pending: { label: 'Pending review', variant: 'selfrep' },
  self_reported: { label: 'Self-reported', variant: 'selfrep' },
  rejected: { label: 'Rejected', variant: 'selfrep' },
};

const TIER_LABELS: Record<string, string> = {
  state: 'State · $85/hr',
  top300: 'Top 300 · $110/hr',
  championship: 'Championship · $150/hr',
};

interface Session {
  id: string;
  scheduledAt: string;
  duration: number;
  status: string;
  meetingNotes?: string;
  amountPaid?: number;
  topic?: string;
  student?: { id: string; name: string; profilePicture?: string | null };
}

const needsConfirmation = (status: string) =>
  status === 'scheduled' || status === 'pending' || status === 'pending_confirmation';

const formatSessionTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Placeholder alternative times offered when proposing a new time.
const buildProposeSlots = (fromStr: string): { label: string; iso: string }[] => {
  const from = new Date(fromStr);
  const offsets = [
    { days: 1, hour: 16, minute: 30 },
    { days: 2, hour: 10, minute: 0 },
    { days: 3, hour: 11, minute: 0 },
  ];
  return offsets.map(({ days, hour, minute }) => {
    const d = new Date(from);
    d.setDate(d.getDate() + days);
    d.setHours(hour, minute, 0, 0);
    return {
      iso: d.toISOString(),
      label: d.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
    };
  });
};

const canJoin = (s: Session) => {
  const mins = (new Date(s.scheduledAt).getTime() - Date.now()) / 60000;
  return mins <= 15 && mins >= -120;
};

const MentorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [profile, setProfile] = useState<MentorProfile | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [proposingId, setProposingId] = useState<string | null>(null);
  const [pickedSlot, setPickedSlot] = useState<string | null>(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [docUploadingId, setDocUploadingId] = useState<string | null>(null);

  const fetchMentorData = useCallback(async () => {
    try {
      const profileRes = await apiService.get('/mentors/my-profile');
      if (profileRes.success) setProfile(profileRes.data);
      try {
        const sessionsRes = await apiService.get('/mentors/my-sessions');
        if (sessionsRes.success) setSessions(sessionsRes.sessions || []);
      } catch {
        setSessions([]);
      }
    } catch (err) {
      console.error('Error fetching mentor data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (location.state?.applicationSubmitted) {
      setShowSubmitted(true);
      window.history.replaceState({}, document.title);
    }
    fetchMentorData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const now = Date.now();
  const requests = sessions.filter(
    (s) => needsConfirmation(s.status) && new Date(s.scheduledAt).getTime() > now
  );
  const upcoming = sessions
    .filter((s) => s.status === 'confirmed' || needsConfirmation(s.status))
    .filter((s) => new Date(s.scheduledAt).getTime() > now - 2 * 60 * 60 * 1000)
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
  const completed = sessions.filter((s) => s.status === 'completed');
  const earnings = Math.round(completed.length * (profile?.pricePerSession || 0) * 0.8);

  const handleConfirm = async (sessionId: string) => {
    const originalStatus = sessions.find((s) => s.id === sessionId)?.status;
    // Optimistic update, revert on failure
    setSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, status: 'confirmed' } : s))
    );
    const result = await apiService.confirmSession(sessionId);
    if (!result.success) {
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId ? { ...s, status: originalStatus || 'scheduled' } : s
        )
      );
      alert(result.error || 'Failed to confirm session');
    }
  };

  const handlePropose = async (session: Session) => {
    if (!pickedSlot) return;
    setRescheduling(true);
    const result = await apiService.rescheduleSession(session.id, pickedSlot);
    setRescheduling(false);
    if (result.success) {
      setProposingId(null);
      setPickedSlot(null);
      fetchMentorData();
    } else {
      alert(result.error || 'Failed to propose new time');
    }
  };

  // Upload (or replace) a proof document for one credential
  const handleDocUpload = async (achievementId: string, file: File | undefined) => {
    if (!file) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setDocUploadingId(achievementId);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const formData = new FormData();
      formData.append('document', file);
      const uploadRes = await fetch(`${API_URL}/mentors/upload-credential`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const uploadData = await uploadRes.json().catch(() => ({}));
      if (!uploadRes.ok || !uploadData.success || !uploadData.url) {
        alert(uploadData.error || 'Failed to upload document');
        return;
      }
      const attachRes = await apiService.post(`/mentors/achievements/${achievementId}/documents`, {
        url: uploadData.url,
        fileName: file.name,
      });
      if (!attachRes.success) {
        alert(attachRes.message || 'Failed to attach document');
        return;
      }
      fetchMentorData();
    } catch (err) {
      console.error('Error uploading proof document:', err);
      alert('Failed to upload document');
    } finally {
      setDocUploadingId(null);
    }
  };

  const handleDocDelete = async (achievementId: string, documentId: string) => {
    const result = await apiService.delete(
      `/mentors/achievements/${achievementId}/documents/${documentId}`
    );
    if (!result.success) {
      alert(result.message || 'Failed to remove document');
      return;
    }
    fetchMentorData();
  };

  if (loading) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
            Mentor dashboard
          </span>
          <h1 style={{ fontSize: 30 }}>Loading…</h1>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap" style={{ maxWidth: 560 }}>
          <div className="card" style={{ padding: 36 }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
              Mentor dashboard
            </span>
            <h1 style={{ fontSize: 26, marginBottom: 10 }}>No mentor profile yet</h1>
            <p className="meta" style={{ marginBottom: 20 }}>
              Finish the application and your dashboard unlocks — requests, payouts, and your
              public profile all live here.
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/become-a-mentor')}>
              Start the application
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section" style={{ paddingTop: 40 }}>
      <div className="wrap">
        {showSubmitted && (
          <div className="note" style={{ marginBottom: 24 }}>
            <strong>Application submitted.</strong> Our team reviews credentials within 24–48
            hours — you'll get an email when your profile goes live. In the meantime you can{' '}
            <Link to="/mentor/profile" style={{ textDecoration: 'underline' }}>
              polish your profile
            </Link>
            .
          </div>
        )}

        <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
          Mentor · {profile.displayName || user?.name}
        </span>
        <h1 style={{ fontSize: 30 }}>Your mentor dashboard</h1>
        <p style={{ color: 'var(--ink-secondary)', marginTop: 8 }}>
          {requests.length > 0
            ? `${requests.length} request${requests.length === 1 ? '' : 's'} waiting. `
            : 'No requests waiting. '}
          Keep your availability current so families can find a time that works.
        </p>

        <div className="stat-row" style={{ margin: '28px 0' }}>
          <div className="card">
            <div className="stat">
              <div className="num plain">${earnings.toLocaleString()}</div>
              <div className="lbl">Earned to date · you keep 80%</div>
            </div>
          </div>
          <div className="card">
            <div className="stat">
              <div className="num plain">{completed.length}</div>
              <div className="lbl">Sessions completed</div>
            </div>
          </div>
          <div className="card">
            <div className="stat">
              <div className="num outcome">
                {profile.averageRating ? profile.averageRating.toFixed(1) : '—'}
              </div>
              <div className="lbl">
                Average rating{profile.totalReviews ? ` (${profile.totalReviews} reviews)` : ''}
              </div>
            </div>
          </div>
          <div className="card">
            <div className="stat">
              <div className="num plain">—</div>
              <div className="lbl">Rebook rate · tracking soon</div>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr',
            gap: 32,
            alignItems: 'start',
          }}
        >
          <div>
            {/* Requests */}
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 14 }}>
                Requests{' '}
                {requests.length > 0 && <span className="tag solid">{requests.length} waiting</span>}
              </h3>
              {requests.length === 0 ? (
                <p className="meta">
                  No pending requests. New bookings show up here for you to confirm.
                </p>
              ) : (
                requests.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      border: '1px solid var(--hairline)',
                      borderRadius: 8,
                      padding: 16,
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 16,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {s.topic || 'Mentor session'} — {s.student?.name || 'Student'}
                        </div>
                        <div className="meta">
                          {formatSessionTime(s.scheduledAt)} · {s.duration || 60} min
                          {s.amountPaid ? ` · $${s.amountPaid}` : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleConfirm(s.id)}>
                          Confirm
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setProposingId(proposingId === s.id ? null : s.id);
                            setPickedSlot(null);
                          }}
                        >
                          Propose new time
                        </button>
                      </div>
                    </div>
                    {proposingId === s.id && (
                      <div
                        style={{
                          marginTop: 14,
                          paddingTop: 14,
                          borderTop: '1px solid var(--hairline)',
                        }}
                      >
                        <div className="meta" style={{ marginBottom: 10 }}>
                          Pick a new time — the family confirms from their email, no login needed.
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                          {buildProposeSlots(s.scheduledAt).map((slot) => (
                            <button
                              key={slot.iso}
                              className={`chip${pickedSlot === slot.iso ? ' on' : ''}`}
                              onClick={() => setPickedSlot(slot.iso)}
                            >
                              {slot.label}
                            </button>
                          ))}
                        </div>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={!pickedSlot || rescheduling}
                          style={!pickedSlot ? { opacity: 0.5 } : undefined}
                          onClick={() => handlePropose(s)}
                        >
                          {rescheduling ? 'Sending…' : 'Send proposal'}
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
              <p className="meta" style={{ marginTop: 10 }}>
                Families can accept a proposed time from the email — a magic link, no login.
              </p>
            </div>

            {/* Upcoming */}
            <div className="card">
              <h3 style={{ marginBottom: 14 }}>Upcoming</h3>
              {upcoming.length === 0 ? (
                <p className="meta">Nothing scheduled. Confirmed sessions appear here.</p>
              ) : (
                <table className="data">
                  <tbody>
                    {upcoming.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>
                            {s.topic || 'Mentor session'} — {s.student?.name || 'Student'}
                          </div>
                          <div className="meta">
                            {formatSessionTime(s.scheduledAt)}
                            {needsConfirmation(s.status) ? ' · awaiting your confirmation' : ''}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {needsConfirmation(s.status) ? (
                            <span className="tag">Pending</span>
                          ) : canJoin(s) ? (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => navigate(`/video-room/mentor/${s.id}`)}
                            >
                              Open room
                            </button>
                          ) : (
                            <span className="meta">Room opens 15 min before</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <aside>
            {/* Payouts — placeholder until payout reporting exists */}
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 12 }}>Payouts</h3>
              <table className="data">
                <tbody>
                  <tr>
                    <td>Earned to date</td>
                    <td className="num-cell" style={{ textAlign: 'right' }}>
                      ${earnings.toLocaleString()}.00
                    </td>
                  </tr>
                  <tr>
                    <td>Pending ({requests.length} session{requests.length === 1 ? '' : 's'})</td>
                    <td className="num-cell" style={{ textAlign: 'right' }}>
                      ${Math.round(requests.length * (profile.pricePerSession || 0) * 0.8)}.00
                    </td>
                  </tr>
                  <tr>
                    <td className="meta">Stripe · deposits every Friday</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 8 }}>Application status</h3>
              <div style={{ marginBottom: 12 }}>
                {profile.isVerified ? (
                  <Stamp variant="verified">Approved — live in marketplace</Stamp>
                ) : (
                  <Stamp variant="selfrep">In review — credentials checked within 24–48 hrs</Stamp>
                )}
              </div>
              {(profile.achievements || []).map((a) => {
                const status = CLAIM_STATUS[a.verificationStatus || 'pending'] || CLAIM_STATUS.pending;
                const docs = a.documents || [];
                return (
                  <div
                    key={a.id}
                    style={{
                      borderTop: '1px solid var(--hairline)',
                      padding: '10px 0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 8,
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 500 }}>{a.title}</div>
                        {a.activityTier && (
                          <div className="meta">{TIER_LABELS[a.activityTier] || a.activityTier}</div>
                        )}
                      </div>
                      <Stamp variant={status.variant}>{status.label}</Stamp>
                    </div>
                    {docs.map((d) => (
                      <div
                        key={d.id}
                        className="meta"
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 8,
                          alignItems: 'center',
                          marginTop: 6,
                        }}
                      >
                        <span style={{ overflowWrap: 'anywhere' }}>
                          {d.fileName}
                          {d.status && d.status !== 'pending' ? ` · ${d.status}` : ''}
                        </span>
                        {(!d.status || d.status === 'pending') && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleDocDelete(a.id, d.id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    {a.verificationStatus !== 'verified' && (
                      <div style={{ marginTop: 8 }}>
                        {docUploadingId === a.id ? (
                          <span className="meta">Uploading…</span>
                        ) : (
                          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                            {docs.length > 0 ? 'Upload replacement' : 'Upload proof'}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                              style={{ display: 'none' }}
                              onChange={(e) => handleDocUpload(a.id, e.target.files?.[0])}
                            />
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="meta" style={{ marginTop: 12 }}>
                Verified credentials set your rate. Full-grid profiles book about twice as often.{' '}
                <Link to="/mentor/profile" style={{ textDecoration: 'underline' }}>
                  Edit profile →
                </Link>
              </p>
            </div>

            <div className="card warm">
              <h3 style={{ fontSize: 16, marginBottom: 8 }}>Refer your parents</h3>
              <p className="meta" style={{ marginBottom: 12 }}>
                Veteran parents earn $75–120/hour answering the investment question. One
                household, two mentors.
              </p>
              <Link className="btn btn-secondary btn-sm" to="/become-a-mentor">
                Send them the application
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default MentorDashboard;
